import { DEFAULT_VALUES, RUNTIME_DATA_EXAMPLE } from '../constants';

class ApiService {
  constructor() {
    this.baseUrl = DEFAULT_VALUES.SERVER_URL;
  }

  // Authentication
  async login(userIdentity, appId, apiKey, language = 'en-US') {
    try {
      const response = await fetch(`${this.baseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey
        },
        body: JSON.stringify({
          user_identity: userIdentity,
          app_id: appId,
          language: language
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async getAuthStatus(apiKey) {
    try {
      const response = await fetch(`${this.baseUrl}/auth/status`, {
        method: 'GET',
        headers: {
          'X-API-Key': apiKey
        }
      });

      if (!response.ok) {
        throw new Error('Status check failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Status check error:', error);
      throw error;
    }
  }

  // Runtime Data
  async sendRuntimeData(sessionId, runtimeData = null) {
    console.log(`🚀 ATTEMPTING TO SEND RUNTIME DATA for session: ${sessionId}`);
    try {
      // Get the data or use example
      const rawData = runtimeData || RUNTIME_DATA_EXAMPLE;
      
      // Format the data to match backend expectations
      const formattedData = {
        data: {
          components: rawData.components || {}
        },
        components: rawData.components || {},
        deviceId: rawData.deviceId || 'unknown-device',
        currentRoute: rawData.currentRoute || 'UNKNOWN_ROUTE'
      };
      
      console.log('📤 Sending formatted runtime data:', formattedData);
      
      const response = await fetch(`${this.baseUrl}/api/runtime/data/${sessionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formattedData)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        const errorMessage = error.detail || error.message || 'Runtime data send failed';
        throw new Error(Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Runtime data error:', error);
      throw error;
    }
  }

  // Health Check
  async healthCheck() {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      if (!response.ok) {
        throw new Error('Health check failed');
      }
      return await response.json();
    } catch (error) {
      console.error('Health check error:', error);
      throw error;
    }
  }

  // Persona Management
  async processAudio(audioBlob, questionId, questionText) {
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.wav');
      formData.append('question_id', questionId);
      formData.append('question_text', questionText);

      const response = await fetch(`${this.baseUrl}/api/persona/process-audio`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Audio processing failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Audio processing error:', error);
      throw error;
    }
  }

  async createPersona(answers, questions, profileLinks = null) {
    try {
      const requestBody = {
        answers: answers,
        questions: questions
      };

      // Add profile links if provided
      if (profileLinks) {
        requestBody.profile_links = profileLinks;
      }

      const response = await fetch(`${this.baseUrl}/api/persona/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Persona creation failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Persona creation error:', error);
      throw error;
    }
  }

  async getPersona(personaId) {
    try {
      const response = await fetch(`${this.baseUrl}/api/persona/${personaId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch persona');
      }
      return await response.json();
    } catch (error) {
      console.error('Get persona error:', error);
      throw error;
    }
  }

  async listPersonas(skip = 0, limit = 10) {
    try {
      const response = await fetch(`${this.baseUrl}/api/persona?skip=${skip}&limit=${limit}`);
      if (!response.ok) {
        throw new Error('Failed to list personas');
      }
      return await response.json();
    } catch (error) {
      console.error('List personas error:', error);
      throw error;
    }
  }

  async updatePersonaLanguage(personaId, language) {
    try {
      const response = await fetch(`${this.baseUrl}/api/persona/voice-chat/update-language/${personaId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          persona_id: personaId,
          language: language
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update language');
      }

      return await response.json();
    } catch (error) {
      console.error('Update persona language error:', error);
      throw error;
    }
  }
}

export const apiService = new ApiService(); 